<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { RouterView, useRoute } from "vue-router";
import { useMainStore } from "@/stores/main.js";
import MobileApp from "@/components/m/MobileApp.vue";
import DemoNudgeModal from "@/components/bt/DemoNudgeModal.vue";
import { useAppHeight } from "@/composables/useAppHeight.js";

const route = useRoute();
const mainStore = useMainStore();

// Publish --app-h for every layout (login/onboarding included), not just the
// authenticated shell — they all live inside the zoomed #app on 4K@100%.
useAppHeight();

/* Reactive viewport check — phones / narrow windows get the native mobile app */
const isMobile = ref(false);
let mql = null;
const apply = (e) => {
  isMobile.value = e.matches;
};
onMounted(() => {
  mql = window.matchMedia("(max-width: 767px)");
  isMobile.value = mql.matches;
  // addEventListener is the modern API; addListener is the Safari fallback
  if (mql.addEventListener) mql.addEventListener("change", apply);
  else mql.addListener(apply);
});
onBeforeUnmount(() => {
  if (!mql) return;
  if (mql.removeEventListener) mql.removeEventListener("change", apply);
  else mql.removeListener(apply);
});

const isAuthenticated = computed(() => mainStore.userEmail !== null);
const isActivated = computed(() => mainStore.userActivated === true);

/* Routes the native mobile shell (MobileApp + bottom tabs) renders itself.
   Every other authenticated route falls through to the responsive
   RouterView / LayoutAuthenticated so phones can still reach Priority Inbox,
   Social Buzz, Analytics, Configuration, Team, Admin and Profile. */
const MOBILE_SHELL_ROUTES = new Set([
  "dashboard",
  "reviews",
  "topics",
  "entities",
  "competitors",
]);

/* Show the mobile shell only inside the authenticated app, and only for the
   handful of primary routes it owns — login, signup, activation, error and
   the secondary pages keep their existing responsive layouts. */
const showMobileApp = computed(
  () =>
    isMobile.value &&
    Boolean(route.meta?.requiresAuth) &&
    isAuthenticated.value &&
    isActivated.value &&
    MOBILE_SHELL_ROUTES.has(route.name),
);
</script>

<template>
  <MobileApp v-if="showMobileApp" />
  <RouterView v-else />
  <DemoNudgeModal />
</template>
